import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowRight,
  BarChart3,
  Brain,
  Check,
  CheckCircle2,
  Clock3,
  FileSearch,
  IndianRupee,
  Lightbulb,
  Loader2,
  Route,
  ShieldCheck,
  Sparkles,
  Target,
  TrendingUp,
} from 'lucide-react';
import AppHeader from '../components/AppHeader';
import { useAuth } from '../lib/auth';
import { api } from '../lib/api';

const INDUSTRY_OPPORTUNITIES = {
  'Agriculture & Agribusiness': ['demand and yield forecasting', 'quality inspection', 'supply-chain planning'],
  'Automotive & Mobility': ['predictive maintenance', 'quality control', 'service forecasting'],
  'Construction & Real Estate': ['project-risk monitoring', 'document intelligence', 'lead qualification'],
  'Consumer Goods & Retail': ['demand forecasting', 'inventory optimization', 'customer engagement'],
  'Education & Training': ['learner support', 'content operations', 'student-retention insights'],
  'Energy & Utilities': ['asset monitoring', 'load forecasting', 'field-service optimization'],
  'Financial Services': ['document processing', 'risk triage', 'customer-service assistance'],
  'Healthcare & Life Sciences': ['operational planning', 'document workflows', 'patient-service support'],
  'Hospitality & Tourism': ['demand forecasting', 'guest engagement', 'revenue optimization'],
  'Information Technology & IT-enabled Services': ['service-desk automation', 'knowledge retrieval', 'delivery intelligence'],
  'Logistics & Transportation': ['route optimization', 'shipment visibility', 'capacity forecasting'],
  Manufacturing: ['predictive maintenance', 'visual quality inspection', 'production planning'],
  'Media & Entertainment': ['content operations', 'audience insights', 'campaign optimization'],
  'Professional Services': ['knowledge retrieval', 'document automation', 'client-service assistance'],
  Telecommunications: ['service assurance', 'customer support', 'network planning'],
  Other: ['customer operations', 'document workflows', 'planning and forecasting'],
};

const maturityLevels = [
  { title: 'AI Leaders', description: 'Scaling governed AI across core operations', width: '42%', tone: 'bg-[#002FA7] text-white' },
  { title: 'Operational Adopters', description: 'Deploying repeatable, measurable use cases', width: '56%', tone: 'bg-[#2454C6] text-white' },
  { title: 'Experimenters', description: 'Testing tools and validating business value', width: '70%', tone: 'bg-[#6E8EDE] text-white' },
  { title: 'Explorers', description: 'Building awareness, data readiness and priorities', width: '84%', tone: 'bg-[#CBD7F5] text-zinc-950' },
  { title: 'Yet to Begin', description: 'Interested, but without a structured starting point', width: '100%', tone: 'bg-zinc-200 text-zinc-800' },
];

const freeItems = [
  'Business overview generated from your inputs',
  'Your organization’s overall AI-readiness level',
  'A concise view of the strengths and gaps identified',
  'Viewable in the portal after completing the assessment',
];

const paidItems = [
  'Industry-specific AI trends, opportunities and data requirements',
  'Data-health analysis when a usable sample is provided',
  'Readiness gaps, recommendations, timeline and next steps',
  'Relevant government funding schemes when applicable',
  'Downloadable, branded Word report',
];

export default function Welcome() {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const [preference, setPreference] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const opportunities = useMemo(
    () => INDUSTRY_OPPORTUNITIES[user?.industry_segment] || INDUSTRY_OPPORTUNITIES.Other,
    [user?.industry_segment],
  );

  const beginAssessment = async () => {
    if (preference === null) {
      setError('Please choose whether you would like to see this introduction after future logins.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const response = await api.put('/auth/preferences/welcome', { show_after_login: preference });
      setUser(response.data);
      localStorage.setItem('startin_user', JSON.stringify(response.data));
      navigate('/assessment');
    } catch (requestError) {
      setError(requestError?.response?.data?.detail || 'We could not save your preference. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-white text-zinc-950">
      <AppHeader />

      <main>
        <section className="relative overflow-hidden border-b border-zinc-200 bg-zinc-950 text-white">
          <div className="absolute inset-0 bg-grid opacity-10" />
          <div className="absolute -right-40 -top-40 h-[34rem] w-[34rem] rounded-full bg-[#002FA7] blur-3xl opacity-60" />
          <div className="relative mx-auto grid max-w-[1400px] gap-12 px-6 py-16 lg:grid-cols-12 lg:px-10 lg:py-24">
            <div className="lg:col-span-7">
              <div className="mb-7 inline-flex items-center gap-2 border border-white/20 px-3 py-1.5 text-[10px] uppercase tracking-[0.2em] text-zinc-300">
                <Sparkles className="h-3.5 w-3.5 text-[#8EAAFF]" /> Your AI readiness journey starts here
              </div>
              <p className="font-mono text-xs uppercase tracking-[0.18em] text-[#AFC1FF]">Dear {user?.company_name || 'team'} team,</p>
              <h1 className="mt-4 max-w-4xl font-display text-4xl font-extrabold leading-[1.05] tracking-tighter sm:text-5xl lg:text-6xl">
                Congratulations on turning AI ambition into a practical first step.
              </h1>
              <p className="mt-7 max-w-2xl text-lg leading-relaxed text-zinc-300">
                Only about one in four surveyed Indian MSMEs report having integrated AI into their operations. The organizations most likely to benefit are not necessarily the ones moving fastest—they are the ones choosing the right problems, data and safeguards before they invest. That is exactly what this assessment helps you do.
              </p>
              <div className="mt-9 flex flex-wrap gap-3 text-xs text-zinc-300">
                <span className="border border-white/20 px-3 py-2">Personalized for {user?.industry_segment || 'your industry'}</span>
                <span className="border border-white/20 px-3 py-2">Structured for Indian MSMEs</span>
                <span className="border border-white/20 px-3 py-2">Designed for practical decisions</span>
              </div>
            </div>

            <div className="lg:col-span-5 lg:pl-4">
              <div className="border border-white/15 bg-white/[0.06] p-6 backdrop-blur-sm">
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-400">Your first milestone</div>
                    <div className="mt-2 font-display text-2xl font-bold">From interest to evidence</div>
                  </div>
                  <Target className="h-8 w-8 text-[#8EAAFF]" strokeWidth={1.4} />
                </div>
                <div className="mt-8 space-y-5">
                  {[
                    ['01', 'Clarify business priorities'],
                    ['02', 'Examine data and technology readiness'],
                    ['03', 'Identify gaps, risks and next actions'],
                  ].map(([number, label]) => (
                    <div key={number} className="flex items-center gap-4 border-t border-white/10 pt-4">
                      <span className="font-mono text-xs text-[#8EAAFF]">{number}</span>
                      <span className="text-sm text-zinc-200">{label}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="mx-auto max-w-[1400px] px-6 py-20 lg:px-10 lg:py-24">
          <SectionHeading>The maturity landscape</SectionHeading>
          <div className="mt-12 grid gap-12 lg:grid-cols-12">
            <div className="lg:col-span-5">
              <h2 className="font-display text-4xl font-bold tracking-tight">Every AI leader started at the base.</h2>
              <p className="mt-5 max-w-xl leading-relaxed text-zinc-600">
                AI maturity is not about buying the newest tool. It grows through deliberate stages—from awareness and experimentation to repeatable, governed business impact.
              </p>
              <div className="mt-8 border-l-2 border-[#002FA7] bg-blue-50 p-5">
                <div className="text-xs font-semibold uppercase tracking-wider text-[#002FA7]">A point to note</div>
                <p className="mt-2 text-sm leading-relaxed text-zinc-700">
                  This pyramid is a general orientation—not an early rating of {user?.company_name || 'your organization'}. Your organization’s maturity level will be determined only after the assessment evaluates its goals, data, technology and governance readiness.
                </p>
              </div>
            </div>

            <div className="lg:col-span-7">
              <div className="border border-zinc-200 bg-zinc-50 p-5 sm:p-8" aria-label="Five-stage AI maturity pyramid">
                <div className="mb-6 flex items-end justify-between gap-4">
                  <div>
                    <div className="font-display text-lg font-bold">The STArtIn AI maturity pyramid</div>
                    <div className="mt-1 text-xs text-zinc-500">A directional model; your position is determined after assessment.</div>
                  </div>
                  <TrendingUp className="hidden h-6 w-6 text-[#002FA7] sm:block" />
                </div>
                <div className="flex flex-col items-center gap-1.5">
                  {maturityLevels.map((level) => (
                    <div key={level.title} style={{ width: level.width }} className={`${level.tone} px-3 py-3 text-center transition-transform hover:scale-[1.01]`}>
                      <div className="text-xs font-semibold sm:text-sm">{level.title}</div>
                      <div className="mt-0.5 hidden text-[10px] opacity-80 sm:block">{level.description}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="border-y border-zinc-200 bg-zinc-50">
          <div className="mx-auto max-w-[1400px] px-6 py-20 lg:px-10 lg:py-24">
            <SectionHeading>The adoption signal</SectionHeading>
            <div className="mt-12 grid gap-10 lg:grid-cols-12">
              <div className="lg:col-span-4">
                <h2 className="font-display text-4xl font-bold tracking-tight">Interest is high. Operational adoption is still catching up.</h2>
                <p className="mt-5 leading-relaxed text-zinc-600">
                  That gap is an opportunity for organizations that build readiness now—before scattered experiments become expensive habits.
                </p>
              </div>

              <div className="grid gap-px bg-zinc-200 sm:grid-cols-2 lg:col-span-8 lg:grid-cols-3">
                <AdoptionStatCard
                  icon={Brain}
                  value="57%"
                  label="of surveyed Indian MSMEs view AI as a core tool for accelerating business growth."
                  source="Vi Business · 2026"
                  href="https://www.financialexpress.com/business/news/msmes-deepen-digital-push-as-ai-adoption-gathers-pace-vi-business-reportnbsp/4277191/"
                />
                <AdoptionStatCard
                  icon={BarChart3}
                  value="25%"
                  label="have already integrated AI into business practices and operational workflows."
                  source="Vi Business · 2026"
                  href="https://www.financialexpress.com/business/news/msmes-deepen-digital-push-as-ai-adoption-gathers-pace-vi-business-reportnbsp/4277191/"
                />
                <AdoptionStatCard
                  icon={TrendingUp}
                  value="36%"
                  label="of surveyed Indian small businesses invested in AI in 2025, up from 26% a year earlier."
                  source="CPA Australia · 2025"
                  href="https://www.cpaaustralia.com.au/about-cpa-australia/media/media-releases/indian-strongest-growth-since-covid"
                />
                <div className="bg-zinc-950 p-8 text-white sm:col-span-2 lg:col-span-3">
                  <div className="grid gap-8 md:grid-cols-2 md:items-center">
                    <div>
                      <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-400">What this means for {user?.company_name || 'your team'}</div>
                      <p className="mt-3 font-display text-2xl font-bold leading-snug">Readiness—not enthusiasm—is what closes the adoption gap.</p>
                    </div>
                    <div className="text-sm leading-relaxed text-zinc-300">
                      For {user?.industry_segment || 'your industry'}, common starting opportunities include {opportunities.join(', ')}. Your assessment will test which opportunities fit your goals, data and operating reality.
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="mx-auto max-w-[1400px] px-6 py-20 lg:px-10 lg:py-24">
          <SectionHeading>What to expect</SectionHeading>
          <div className="mt-12 grid gap-8 lg:grid-cols-12">
            <div className="lg:col-span-5">
              <h2 className="font-display text-4xl font-bold tracking-tight">A guided evaluation, built around your business.</h2>
              <p className="mt-5 leading-relaxed text-zinc-600">
                You will describe your organization, define priority goals, map the data available to support them and examine your technology and governance foundations.
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <span className="inline-flex items-center gap-2 border border-zinc-200 px-3 py-2 text-xs"><Clock3 className="h-4 w-4 text-[#002FA7]" /> About 15 minutes</span>
                <span className="inline-flex items-center gap-2 border border-zinc-200 px-3 py-2 text-xs"><ShieldCheck className="h-4 w-4 text-[#002FA7]" /> Save and return</span>
              </div>
            </div>
            <div className="grid gap-px bg-zinc-200 sm:grid-cols-2 lg:col-span-7">
              {[
                [FileSearch, 'Company context', 'Your operations, customers, scale and business environment.'],
                [Target, 'Goals and challenges', 'Up to five priorities refined into actionable problem statements.'],
                [BarChart3, 'Data readiness', 'The internal and external sources needed to pursue priority use cases.'],
                [ShieldCheck, 'Technology and governance', 'Infrastructure, workload, regulatory and ethical considerations.'],
              ].map(([Icon, title, text], index) => (
                <div key={title} className="bg-white p-6">
                  <div className="flex items-center justify-between">
                    <Icon className="h-5 w-5 text-[#002FA7]" strokeWidth={1.5} />
                    <span className="font-mono text-[10px] text-zinc-400">0{index + 1}</span>
                  </div>
                  <h3 className="mt-5 font-display text-lg font-semibold">{title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-zinc-600">{text}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-10 grid gap-6 border border-zinc-200 bg-white p-7 md:grid-cols-[auto_1fr] md:items-center">
            <div className="grid h-14 w-14 place-items-center bg-zinc-950 text-white">
              <Brain className="h-6 w-6" />
            </div>
            <div>
              <div className="font-display text-lg font-bold">Five AI models. One coherent assessment.</div>
              <p className="mt-1 text-sm leading-relaxed text-zinc-600">
                Different AI models have different strengths. STArtIn AI uses five models across the analysis, assigning each task to the model best suited to it and bringing their outputs together into one consistent final report.
              </p>
            </div>
          </div>
        </section>

        <section className="border-y border-zinc-200 bg-zinc-50">
          <div className="mx-auto max-w-[1400px] px-6 py-20 lg:px-10 lg:py-24">
            <div className="mx-auto max-w-3xl text-center">
              <SectionHeading>Your outputs</SectionHeading>
              <h2 className="mt-10 font-display text-4xl font-bold tracking-tight">Start free. Go deeper when the evidence is useful.</h2>
              <p className="mt-5 text-zinc-600">The synopsis gives you an immediate orientation. The detailed report turns the same assessment into a more complete, downloadable decision document.</p>
            </div>

            <div className="mt-12 grid gap-6 lg:grid-cols-2">
              <article className="border border-zinc-200 bg-white p-8 lg:p-10">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Free output</div>
                    <h3 className="mt-2 font-display text-3xl font-bold">Your assessment synopsis</h3>
                  </div>
                  <Lightbulb className="h-7 w-7 text-[#002FA7]" strokeWidth={1.5} />
                </div>
                <div className="mt-8 space-y-4">
                  {freeItems.map((item) => <OutputItem key={item}>{item}</OutputItem>)}
                </div>
              </article>

              <article className="relative overflow-hidden border border-[#002FA7] bg-[#002FA7] p-8 text-white lg:p-10">
                <div className="absolute -right-20 -top-20 h-56 w-56 rounded-full bg-white/10 blur-2xl" />
                <div className="relative flex items-start justify-between gap-4">
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.2em] text-[#C9D5FF]">Optional detailed output</div>
                    <h3 className="mt-2 font-display text-3xl font-bold">Your detailed report</h3>
                  </div>
                  <div className="text-right">
                    <div className="font-display text-3xl font-extrabold">₹3,000</div>
                    <div className="mt-1 text-[10px] uppercase tracking-wider text-[#C9D5FF]">Fixed price</div>
                  </div>
                </div>
                <div className="relative mt-8 space-y-4">
                  {paidItems.map((item) => <OutputItem key={item} dark>{item}</OutputItem>)}
                </div>
              </article>
            </div>

            <div className="mt-6 grid gap-6 border border-zinc-200 bg-white p-7 md:grid-cols-[auto_1fr] md:items-center">
              <div className="grid h-14 w-14 place-items-center bg-zinc-950 text-white">
                <IndianRupee className="h-6 w-6" />
              </div>
              <div>
                <div className="font-display text-lg font-bold">Start Smart with STArtIn</div>
                <p className="mt-1 text-sm leading-relaxed text-zinc-600">
                  A manual assessment by consultants can require significant time and budget. We believe organizations should be judicious about making that investment before they know where deeper support will create the most value. STArtIn AI gives you high-level insight into your current AI readiness, the gaps that deserve attention and the areas where your organization should focus its energy. You can then decide whether—and where—a more extensive consulting engagement is warranted.
                </p>
                <p className="mt-3 text-xs text-zinc-500">STArtIn AI is a standardized self-service assessment. It provides an informed starting point and does not replace every form of specialist consulting.</p>
              </div>
            </div>
          </div>
        </section>

        <section className="mx-auto max-w-[1400px] px-6 py-20 lg:px-10 lg:py-24">
          <div className="relative overflow-hidden bg-zinc-950 px-6 py-12 text-white sm:px-10 lg:px-14 lg:py-16">
            <div className="absolute inset-0 bg-grid opacity-10" />
            <div className="relative grid gap-10 lg:grid-cols-12 lg:items-end">
              <div className="lg:col-span-7">
                <Route className="h-7 w-7 text-[#8EAAFF]" strokeWidth={1.5} />
                <h2 className="mt-5 font-display text-4xl font-bold tracking-tight sm:text-5xl">Ready to find your clearest path to AI?</h2>
                <p className="mt-5 max-w-2xl leading-relaxed text-zinc-300">Choose your preference below, then begin the assessment. You can return to this introduction at any time from the navigation.</p>

                <fieldset className="mt-8">
                  <legend className="text-sm font-medium text-white">Would you like to see this introduction after future logins?</legend>
                  <div className="mt-4 flex flex-col gap-3 sm:flex-row">
                    <PreferenceOption selected={preference === true} onSelect={() => { setPreference(true); setError(''); }} label="Yes" description="Show it after every login" />
                    <PreferenceOption selected={preference === false} onSelect={() => { setPreference(false); setError(''); }} label="No" description="Go directly to my assessment" />
                  </div>
                </fieldset>

                {error && <div className="mt-4 text-sm text-red-300" role="alert">{error}</div>}
              </div>

              <div className="lg:col-span-5 lg:text-right">
                <button
                  type="button"
                  onClick={beginAssessment}
                  disabled={preference === null || saving}
                  className="inline-flex w-full items-center justify-center gap-2 bg-white px-8 py-4 text-sm font-semibold text-zinc-950 transition-colors hover:bg-zinc-100 disabled:cursor-not-allowed disabled:opacity-40 sm:w-auto"
                  data-testid="begin-assessment"
                >
                  {saving ? <><Loader2 className="h-4 w-4 animate-spin" /> Saving preference</> : <>Begin Assessment <ArrowRight className="h-4 w-4" /></>}
                </button>
                <div className="mt-3 text-xs text-zinc-500">Your selection can be changed by revisiting this page.</div>
              </div>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}

function OutputItem({ children, dark = false }) {
  return (
    <div className={`flex items-start gap-3 text-sm ${dark ? 'text-blue-50' : 'text-zinc-700'}`}>
      <CheckCircle2 className={`mt-0.5 h-4 w-4 shrink-0 ${dark ? 'text-white' : 'text-[#002FA7]'}`} strokeWidth={1.8} />
      <span>{children}</span>
    </div>
  );
}

function SectionHeading({ children }) {
  return (
    <div className="text-center">
      <div className="inline-block border-b-2 border-[#002FA7] pb-2 font-display text-xl font-bold tracking-tight text-zinc-950 sm:text-2xl">
        {children}
      </div>
    </div>
  );
}

function AdoptionStatCard({ icon: Icon, value, label, source, href }) {
  return (
    <article className="flex min-h-[18rem] flex-col bg-white p-7">
      <div className="flex items-center justify-between">
        <Icon className="h-6 w-6 text-[#002FA7]" strokeWidth={1.5} />
        <span className="font-mono text-[10px] text-zinc-400">INDIA</span>
      </div>
      <div className="mt-8 font-display text-5xl font-extrabold text-[#002FA7]">{value}</div>
      <p className="mt-3 flex-1 text-sm leading-relaxed text-zinc-600">{label}</p>
      <a href={href} target="_blank" rel="noreferrer" className="mt-5 inline-flex items-center gap-1.5 text-[10px] font-medium uppercase tracking-wider text-zinc-500 underline decoration-zinc-300 underline-offset-4 hover:text-[#002FA7]">
        {source} <ArrowRight className="h-3 w-3" />
      </a>
    </article>
  );
}

function PreferenceOption({ selected, onSelect, label, description }) {
  return (
    <label className={`flex min-w-[15rem] cursor-pointer items-center gap-3 border px-4 py-3 transition-colors ${selected ? 'border-[#8EAAFF] bg-white/10' : 'border-white/20 hover:border-white/40'}`}>
      <input type="radio" name="welcome-preference" checked={selected} onChange={onSelect} className="sr-only" />
      <span className={`grid h-5 w-5 shrink-0 place-items-center rounded-full border ${selected ? 'border-[#8EAAFF] bg-[#8EAAFF] text-zinc-950' : 'border-zinc-500'}`}>
        {selected && <Check className="h-3 w-3" strokeWidth={3} />}
      </span>
      <span>
        <span className="block text-sm font-semibold">{label}</span>
        <span className="block text-xs text-zinc-400">{description}</span>
      </span>
    </label>
  );
}
